import { uuid } from "@/utils"
import { union } from "@/utils/set"
import { UUID } from "crypto"
import { ulid } from "ulid"

export class RNum {
  static float (min: number, max: number): number {
    return Math.random() * (max - min) + min
  }

  static int (min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1) + min)
  }
}

export enum PriorityLevel {
  Expedite, Primary, Secondary, Tertiary, Reserve
}

enum MetricLengthUnit {
  Kilometer = 'km',
  Hectometer = 'hm',
  Decameter = 'dam',
  Meter = 'm',
  Decimeter = 'dm',
  Centimeter = 'cm',
  Millimeter = 'mm',
  Micrometer = 'μm',
  Nanometer = 'nm'
}

export enum CardinalDirection {
  North = 'N',
  NorthNorthEast = 'NNE',
  NorthEast = 'NE',
  EastNorthEast = 'ENE',

  East = 'E',
  EastSouthEast = 'ESE',
  SouthEast = 'SE',
  SouthSouthEast = 'SSE',

  South = 'S',
  SouthSouthWest = 'SSW',
  SouthWest = 'SW',
  WestSouthWest = 'WSW',

  West = 'W',
  NorthWestNorth = 'NWN',
  NorthWest = 'NW',
  NorthNorthWest = 'NNW'
}


export class SpatialNode {
  public node: Int32Array
  constructor(x: number, y: number, z: number) {
    this.node = new Int32Array([x, y, z])
  }

  get x () { return this.node[0] }
  set x (n: number) { this.node[0] = n }
  get y () { return this.node[1] }
  set y (n: number) { this.node[1] = n }
  get z () { return this.node[2] }
  set z (n: number) { this.node[2] = n }

  // distance to target vector
  distance (target: SpatialNode): number {
    return Math.sqrt(
      (this.x - target.x) ** 2 +
      (this.y - target.y) ** 2 +
      (this.z - target.z) ** 2
    );
  }
}

// ---

export class BoundingBox {
  constructor(public c1: SpatialNode, public c2: SpatialNode) {
    if (!this.c1 || !this.c2) throw Error('Invalid BoundingBox Bounds')
  }

  update (c1: SpatialNode, c2: SpatialNode) {
    if (!this.c1 || !this.c2) throw Error('Invalid Bounds')
    this.c1 = c1
    this.c2 = c2
  }

  intersects (box: BoundingBox) {
    return !(
      this.c1.x > box.c2.x || this.c2.x < box.c1.x ||
      this.c1.y > box.c2.y || this.c2.y < box.c1.y ||
      this.c1.z > box.c2.z || this.c2.z < box.c1.z
    )
  }

  contains (box: BoundingBox): boolean {
    return (
      this.c1.x <= box.c1.x && this.c2.x >= box.c2.x &&
      this.c1.y <= box.c1.y && this.c2.y >= box.c2.y &&
      this.c1.z <= box.c1.z && this.c2.z >= box.c2.z
    );
  }

  union (root: SpatialBoundary, box: BoundingBox): BoundingBox {
    return new BoundingBox(
      root.get(Math.min(this.c1.x, box.c1.x), Math.min(this.c1.y, box.c1.y), Math.min(this.c1.z, box.c1.z)),
      root.get(Math.max(this.c2.x, box.c2.x), Math.max(this.c2.y, box.c2.y), Math.max(this.c2.z, box.c2.z))
    )
  }
}

// ---

export class SpatialEntity {
  public uuid: UUID

  // tuple containing 2 spatial points describing a "hitbox" between the two, for the location, i.e 1,1,3 > 1,2,3 = location of 2 wide, 1 deep and 3 high
  public boundingBox: BoundingBox

  public type: 'rack' | 'table' | 'container' | 'open' // open meaning any location that is not organised

  public active: boolean

  public ref: any

  // NOTE: these are determined by the
  // public width: number
  // public depth: number
  // public height: number
  // public unit: MetricLengthUnit

  public direction?: CardinalDirection

  // TODO: mvoe metadata to an inheriting class...
  public primary_construction_material?: string // advisory only
  public hazards?: string
  public notes?: string

  // --
  public id?: string
  public count?: number // item count
  public capacity?: number // max count
  public priority?: PriorityLevel

  public min_count?: number // for highlighting

  public error?: boolean // if location has an active issue(-event)

  constructor(public boundary: SpatialBoundary, c1: SpatialNode | BoundingBox, c2: SpatialNode, metadata?: { id: string, count?: number, min_count?: number, capacity?: number, priority?: PriorityLevel }) {
    if (!c1 || (!(c1 instanceof BoundingBox) && !c2)) throw Error('Invalid Entity Bounds')
    this.uuid = uuid()
    this.boundingBox = c1 instanceof BoundingBox ? c1 : new BoundingBox(c1, c2)

    // TODO: setter...
    this.type = 'rack'
    this.active = false

    this.id = metadata?.id
    this.count = metadata?.count
    this.capacity = metadata?.capacity
  }

  // x differential
  get width () {
    return this.boundingBox.c2.x - this.boundingBox.c1.x
  }

  // y differential
  get length () {
    return this.boundingBox.c2.y - this.boundingBox.c1.y
  }

  // z differential
  get height () {
    return this.boundingBox.c2.z - this.boundingBox.c1.z
  }

  // returns all positional entities (i.e coördinates) covered by this location, based on this.boundingBox
  get nodesInBoundingBox () {
    const p1 = this.boundingBox.c1
    const p2 = this.boundingBox.c2
    const points: SpatialNode[] = []

    for (let x = Math.min(p1.x, p2.x); x <= Math.max(p1.x, p2.x); x++) {
      for (let y = Math.min(p1.y, p2.y); y <= Math.max(p1.y, p2.y); y++) {
        for (let z = Math.min(p1.z, p2.z); z <= Math.max(p1.z, p2.z); z++) {
          points.push(new SpatialNode(x, y, z))
        }
      }
    }

    return points
  }

  updateBoundingBox (start: SpatialNode | [number, number, number], end: SpatialNode | [number, number, number]) {
    const ps = this.boundary.get((start as SpatialNode).x ?? start[0], (start as SpatialNode).y ?? start[1], (start as SpatialNode).z ?? start[2])
    const pe = this.boundary.get((end as SpatialNode).x ?? end[0], (end as SpatialNode).y ?? end[1], (end as SpatialNode).z ?? end[2])

    if (!ps || !pe) throw Error('Invalid positional coördinates')

    this.boundingBox.update(ps, pe)
  }

  // returns if the two entities intersect
  static intersect () {
    //
  }
}

// ---
const DEFAULT_HALF_SIZE = 20
class Octree {
  private _root: OctantNode
  constructor(root: SpatialBoundary, origin: SpatialNode, halfSize = DEFAULT_HALF_SIZE, threshold = 8) {
    this._root = new OctantNode(root, origin, halfSize, threshold)
  }

  get root () {
    return this._root
  }
}

class CachedOctantNode {
  constructor(public node: SpatialEntity, public accessCount = 0) { }
}

class OctantNode {

  public root: SpatialBoundary
  public entities: Array<CachedOctantNode>
  public children: [OctantNode, OctantNode, OctantNode, OctantNode, OctantNode, OctantNode, OctantNode, OctantNode] | []

  public parent?: OctantNode
  public cacheThreshold = 10

  public boundingBox: BoundingBox

  // default threshold = 1 per octant
  constructor(root: SpatialBoundary | OctantNode, public origin: SpatialNode, public halfSize = DEFAULT_HALF_SIZE, public threshold = 8) {
    if (root instanceof OctantNode) this.root = root.root
    else this.root = root

    this.entities = []
    this.children = []

    // create a bounding box based on the origin node as the center and halfsize
    this.boundingBox = OctantNode.BoundingBox(this.root, origin, halfSize)
  }

  static BoundingBox (root: SpatialBoundary, origin: SpatialNode, halfSize: number) {
    // console.log(Math.min(root.maxX, Math.max(root.minX, origin.x - halfSize)), Math.max(root.minX, Math.min(root.maxX, origin.x + halfSize)))
    const c1 = root.get(Math.min(root.maxX, Math.max(root.minX, origin.x - halfSize)), Math.min(root.maxY, Math.max(root.minY, origin.y - halfSize)), Math.min(root.maxZ, Math.max(root.minZ, origin.z - halfSize)))
    const c2 = root.get(Math.max(root.minX, Math.min(root.maxX, origin.x + halfSize)), Math.max(root.minY, Math.min(root.maxY, origin.y + halfSize)), Math.max(root.minZ, Math.min(root.maxZ, origin.z + halfSize)))

    return new BoundingBox(c1, c2)
  }

  get isDivided () {
    return this.children?.length > 0
  }

  // TODO: refactor to insert entities with bounding boxes instead of single points
  insert (entity: SpatialEntity) {
    // If the entity is already in the cache, no need to insert further
    for (const op of this.entities) {
      if (this.equalsBounds(op.node, entity)) {
        op.accessCount++
        return true
      }
    }

    // Check if the point is within the bounds, or intersects the bounds of the current node
    // if (!this.containsEntity(entity) ) return false
    if (!this.boundingBox.intersects(entity.boundingBox)) return false

    // If there is room in this node and it hasn't subdivided yet, add the point here
    if (!this.isDivided && (this.entities.length < this.threshold)) {
      this.entities.push(new CachedOctantNode(entity))
      return true
    }

    // If the node has reached capacity and hasn't subdivided, subdivide it now
    if (!this.isDivided) {
      this.divide()

      // Move existing points into the appropriate child nodes
      const oldPoints = [...this.entities]
      this.entities = [] // Clear current points as they will be redistributed
      for (const p of oldPoints) {
        this.insert(p.node) // Reinsert old points into the appropriate child nodes
      }
    }

    // Insert the new point into the appropriate child node
    for (const child of this.children) {
      if (child.insert(entity)) return true
    }

    // should never be reached
    return false
  }

  // subdivide into child-nodes & redistribute points, keeping high-priority entities
  divide () {
    const { x, y, z } = this.origin
    const half = this.halfSize / 2

    const xmin = Math.max(this.root.minX, x - half)
    const ymin = Math.max(this.root.minY, y - half)
    const zmin = Math.max(this.root.minZ, z - half)

    const xmax = Math.min(this.root.maxX, x + half)
    const ymax = Math.min(this.root.maxY, y + half)
    const zmax = Math.min(this.root.maxZ, z + half)

    // console.log('dividing', this.boundingBox.c1.node, this.boundingBox.c2.node, this.entities.length)

    // get spatial nodes from the root grid, as to not create duplicate point references...
    this.children = [
      new OctantNode(this, this.root.get(xmin, ymin, zmin), half),
      new OctantNode(this, this.root.get(xmax, ymin, zmin), half),
      new OctantNode(this, this.root.get(xmin, ymax, zmin), half),
      new OctantNode(this, this.root.get(xmax, ymax, zmin), half),
      new OctantNode(this, this.root.get(xmin, ymin, zmax), half),
      new OctantNode(this, this.root.get(xmax, ymin, zmax), half),
      new OctantNode(this, this.root.get(xmin, ymax, zmax), half),
      new OctantNode(this, this.root.get(xmax, ymax, zmax), half)
    ]

    // divide entities over child nodes
    const unfit: Array<CachedOctantNode> = []

    while (this.entities.length > 0) {
      const e = this.entities.pop()
      if (!e) break

      const match = this.children.find(c => c.intersect(e.node.boundingBox).length)
      if (!match) {
        // NOTE: should never happen
        console.error(`Could not fit node "${e.node.id}" into any child octant. pushing back to current octant entity list when done`)
        unfit.push(e)
      } else match.insert(e.node)
    }

    this.entities = unfit
  }

  containsEntity (e: SpatialEntity): boolean {
    return (
      e.boundingBox.c1.x >= this.origin.x - this.halfSize &&
      e.boundingBox.c1.x <= this.origin.x + this.halfSize &&
      e.boundingBox.c1.y >= this.origin.y - this.halfSize &&
      e.boundingBox.c1.y <= this.origin.y + this.halfSize &&
      e.boundingBox.c1.z >= this.origin.z - this.halfSize &&
      e.boundingBox.c1.z <= this.origin.z + this.halfSize &&

      e.boundingBox.c2.x >= this.origin.x - this.halfSize &&
      e.boundingBox.c2.x <= this.origin.x + this.halfSize &&
      e.boundingBox.c2.y >= this.origin.y - this.halfSize &&
      e.boundingBox.c2.y <= this.origin.y + this.halfSize &&
      e.boundingBox.c2.z >= this.origin.z - this.halfSize &&
      e.boundingBox.c2.z <= this.origin.z + this.halfSize
    );
  }

  contains (node: SpatialNode): boolean {
    return (
      node.x >= this.origin.x - this.halfSize &&
      node.x <= this.origin.x + this.halfSize &&
      node.y >= this.origin.y - this.halfSize &&
      node.y <= this.origin.y + this.halfSize &&
      node.z >= this.origin.z - this.halfSize &&
      node.z <= this.origin.z + this.halfSize
    );
  }

  equals (p1: SpatialNode, p2: SpatialNode): boolean {
    return p1.x === p2.x && p1.y === p2.y && p2.z === p1.z;
  }

  equalsBounds (p1: SpatialEntity, p2: SpatialEntity): boolean {
    return this.equals(p1.boundingBox.c1, p2.boundingBox.c1) &&
      this.equals(p2.boundingBox.c2, p2.boundingBox.c2)
  }

  promote (node: CachedOctantNode) {
    if (!this.parent) return
    this.parent.cache(node)
  }

  cache (node: CachedOctantNode) {
    if (!this.isDivided) return
    this.entities.push(node)
  }

  // find spatial entity based on input x/y/z (spatial-node)
  findNode (node: SpatialNode): CachedOctantNode | null {
    // First, check if the node is in the top-level cache
    for (const p of this.entities) {
      // FIXME: insertsect node boundingbox instead
      if (this.equals(p.node.boundingBox.c1, node)) {
        p.accessCount++
        return p
      }
    }

    // If the node is within the bounds of this node, search here
    if (this.contains(node)) {
      for (const p of this.entities) {
        if (this.equals(p.node.boundingBox.c1, node) || this.equals(p.node.boundingBox.c2, node)) {
          p.accessCount++

          // Promote to parent cache if it exceeds the access threshold
          if (p.accessCount >= this.cacheThreshold) this.promote(p)

          return p
        }
      }

      // If subdivided, search in child nodes
      if (this.isDivided) {
        for (const child of this.children) {
          const found = child.findNode(node)
          if (found) return found
        }
      }
    }

    return null; // Node not found
  }

  intersect (box: BoundingBox) {
    // console.log('intersecting', box.c1.node, box.c2.node, '\nwith', this.boundingBox.c1.node, this.boundingBox.c2.node, '\nintersects?', this.boundingBox.intersects(box), 'isDivided', this.isDivided)
    if (!this.boundingBox.intersects(box)) return []
    if (!this.isDivided) return [this]

    let intersections = [this as OctantNode]
    for (const child of this.children) {
      intersections = intersections.concat(child.intersect(box))
    }
    return intersections
  }
}

// ---

class EntityController {
  private _boundary: SpatialBoundary

  private _octree: Octree

  constructor(boundary: SpatialBoundary) {
    this._boundary = boundary

    // console.log('creating octree', this._boundary, 'origin', this._boundary.get(0, 0, 0))
    this._octree = new Octree(
      this._boundary,
      this._boundary.get(this._boundary.maxX / 2, this._boundary.maxY / 2, this._boundary.maxZ / 2),
      Math.max(this._boundary.maxZ, Math.max(this._boundary.maxX, this._boundary.maxY)) / 2,
      this._boundary.options?.ocant_threshold
    )
  }

  get octree () {
    return this._octree
  }

  add (id: string, c1: { x: number, y: number, z: number } | [number, number, number] | SpatialNode | BoundingBox, c2: { x: number, y: number, z: number } | [number, number, number] | SpatialNode, ref?: any): SpatialEntity
  add (c1: { x: number, y: number, z: number } | [number, number, number] | SpatialNode | BoundingBox, c2: { x: number, y: number, z: number } | [number, number, number] | SpatialNode, ref?: any): SpatialEntity
  add (idorc1: string | { x: number, y: number, z: number } | [number, number, number] | SpatialNode | BoundingBox, n1: { x: number, y: number, z: number } | [number, number, number] | SpatialNode | BoundingBox, n2?: { x: number, y: number, z: number } | [number, number, number] | SpatialNode, ref?: any): SpatialEntity {
    const c1 = typeof idorc1 === 'string' ? n1 : idorc1
    const c2 = typeof idorc1 === 'string' ? n2 : n1 as SpatialNode
    const id = typeof idorc1 === 'string' ? idorc1 : ulid()

    // TODO: check intersection for existing entities
    if (Array.isArray(c1) && (c1 as any).some(n => n < 0)) throw Error(`Invalid point ${(c1 as any)[0]} ${(c1 as any)[1]} ${(c1 as any)[1]}`)
    const ca = Array.isArray(c1) ? this._boundary.get(c1[0], c1[1], c1[2]) : (typeof c1 === 'object' && !('c1' in c1) && (c1.x && c1.y && c1.z) ? this._boundary.get(c1.x, c1.y, c1.z) : c1 as SpatialNode | BoundingBox)
    const cb = Array.isArray(c2) ? this._boundary.get(c2[0], c2[1], c2[2]) : (typeof c2 === 'object' && (c2.x && c2.y && c2.z) ? this._boundary.get(c2.x, c2.y, c2.z) : c2 as SpatialNode)

    const entity = new SpatialEntity(this._boundary, ca, cb, { id })
    entity.ref = ref
    this._boundary.entities.set(entity.uuid, entity)

    this._octree.root.insert(entity)

    return entity
  }

  remove (uuid) {
    this._boundary.entities.delete(uuid)

    return uuid
  }

  update (uuid, c1, c2) {
    const entity = this._boundary.entities.get(uuid)
    if (!entity) return

    entity.updateBoundingBox(c1, c2)

    return entity
  }

  // TODO: use octant tree to find entity
  find (node: SpatialNode) {
    return this._octree.root.findNode(node)
  }

  // find nodes intersecting with given boundary
  intersect (boundary: BoundingBox) {
    return EntityController.NodesFromOctants(this._octree.root.intersect(boundary), boundary)
  }

  static NodesFromOctants (octants: Array<OctantNode>, boundary: BoundingBox): Set<SpatialEntity> {
    return octants.reduceRight((entities, octant) => {
      // check octant entities for matches
      octant.entities.forEach(e => {
        if (boundary.intersects(e.node.boundingBox)) entities.add(e.node)
      })

      if (octant.children?.length) {
        entities = union<SpatialEntity>(entities, EntityController.NodesFromOctants(octant.children, boundary))
      }

      return entities
    }, new Set<SpatialEntity>())
  }

}

class PointsController {
  constructor(public root: SpatialBoundary) { }

  slice (axis: 'x' | 'y' | 'z', index: number) {
    switch (axis) {
      case 'x':
        return this.root._points[index].flat(1)
      case 'y':
      case 'z':
        return this.root._points.reduce((c, x) => c.concat(x.reduce((cy, y) => cy.concat(y.filter(n => n[axis] === index)), [])), []).flat(1)
      default:
        return this.root._points
    }
  }
}

// ---

export default class SpatialBoundary {
  public _points: Array<Array<Array<SpatialNode>>> // positional matrix

  public minX: number
  public maxX: number

  public minY: number
  public maxY: number

  public minZ: number
  public maxZ: number

  private _entity_controller: EntityController
  private _points_controller: PointsController

  public entities: Map<UUID, SpatialEntity>

  public boundingBox?: BoundingBox

  constructor(x: number, y: number, z: number, public options: { ocant_threshold?: number } = {}) {
    this.minX = 0
    this.maxX = x
    this.minY = 0
    this.maxY = y
    this.minZ = 0
    this.maxZ = z

    this.entities = new Map()

    this._points = []
    this.generate()

    this._entity_controller = new EntityController(this)
    this._points_controller = new PointsController(this)
  }

  get count () {
    return this._points?.reduce((sum, x) => sum + x.reduce((xs, y) => xs + y.length, 0), 0) || 0
  }

  get size () {
    return this.maxX * this.maxY * this.maxZ
  }

  get points () {
    return this._points_controller
  }

  static Layout (mx, my, mz) {
    return Array.from({ length: mx }, (_, x) =>
      Array.from({ length: my }, (_, y) =>
        Array.from({ length: mz }, (_, z) => new SpatialNode(x, y, z))))

    // const points = new Array(mx);

    // for (let x = 0; x < mx; x++) {
    //   const ya = new Array(my);
    //   points[x] = ya;

    //   for (let y = 0; y < my; y++) {
    //     const za = new Array(mz);
    //     ya[y] = za;

    //     for (let z = 0; z < mz; z++) {
    //       za[z] = new SpatialNode(x, y, z);
    //     }
    //   }
    // }

    // return points
  }

  get (x: number, y: number, z = 0): SpatialNode {
    if (!this._points?.length) throw Error('Not Initialized')
    if ((x < this.minX || x > this.maxX) || (y < this.minY || y > this.maxY) || (z < this.minZ || z > this.maxZ))
      throw Error(`Out of Bounds (x${x}/y${y}/z${z}) (max x${this.minX}-${this.maxX}[${(x < this.minX || x > this.maxX) ? '𐄂' : '✓'}] / y${this.minY}-${this.maxY}[${(y < this.minY || y > this.maxY) ? '𐄂' : '✓'}] / z${this.minZ}-${this.maxZ}[${(z < this.minZ || z > this.maxZ) ? '𐄂' : '✓'}])`)
    // console.log(Math.floor(x), Math.floor(y), Math.floor(z), this._points?.[Math.floor(x)]?.[Math.floor(y)]?.[Math.floor(z)])
    return this._points?.[Math.round(x)]?.[Math.round(y)]?.[Math.round(z)]
  }

  // NOTE: generates max + 1 points for every axis, so we get (index 0 + max, to make life easier
  generate () {
    this._points = SpatialBoundary.Layout(this.maxX + 1, this.maxY + 1, this.maxZ + 1)
    this.boundingBox = new BoundingBox(this.get(this.minX, this.minY, this.minZ), this.get(this.maxX, this.maxY, this.maxZ))
  }

  update (newMaxX: number, newMaxY: number, newMaxZ: number) {
    // X
    for (let x = 0; x <= newMaxX; x++) {
      if (!this._points[x]) this._points[x] = new Array(newMaxY)

      // Y
      for (let y = 0; y <= newMaxY; y++) {
        if (!this._points[x][y]) this._points[x][y] = new Array(newMaxZ)

        // Z
        for (let z = 0; z <= newMaxZ; z++) {
          if (!this._points[x][y][z]) this._points[x][y][z] = new SpatialNode(x, y, z)
        }

        if (newMaxZ < this.maxZ) this._points[x][y].length = newMaxZ
      }

      if (newMaxY < this.maxY) this._points[x].length = newMaxY
    }

    // Remove excess x elements if the new x dimension is smaller
    if (newMaxX < this.maxX) this._points.length = newMaxX

    this.maxX = newMaxX
    this.maxY = newMaxY
    this.maxZ = newMaxZ
  }

  // add a new entity, specifying the hitbox
  get entity () {
    return this._entity_controller
  }

  // find intersecting entities for entity / spatial-point
  // findIntersection (entityOrpoint: SpatialNode | SpatialEntity) {
  //   //
  // }

}
